package one.devsbox.www.ui.campaigns.edit

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDate
import one.devsbox.commons.EMPTY
import one.devsbox.commons.nowDate
import one.devsbox.shared.ads.CampaignState
import one.devsbox.shared.ads.SharedIO
import one.devsbox.shared.files.ALL
import one.devsbox.shared.files.ResourceType
import one.devsbox.shared.files.RotationType
import one.devsbox.www.io.Repository
import one.devsbox.www.provider.NavigationCallbackProvider
import one.devsbox.www.www_portal.generated.resources.*
import org.jetbrains.compose.resources.StringResource
import tk.mallumo.compose.navigation.viewmodel.NavigationViewModel
import tk.mallumo.compose.navigation.viewmodel.VM
import tk.mallumo.utils.DataState
import tk.mallumo.utils.noDiacriticsLower
import kotlin.time.Clock

@VM
class CampaignEVM : NavigationViewModel() {
    private lateinit var ctx: CampaignECtx

    var state by mutableStateOf<DataState<CampaignECtx.Editor>>(DataState.Loading())
        private set

    var currentStep by mutableStateOf(0)

    var warningMessage by mutableStateOf<StringResource?>(null)

    private val mediaPickTasks = mutableListOf<String>()

    interface Action {
        fun toggleStartDate()
        fun toggleEndDate()
        fun nextStep()
        fun prevStep()
        fun toggleDevice(deviceId: Long)
        fun saveAsDraft()
        fun activateCampaign()
        fun clearWarning()
        fun pickMedia(aspectRatio: String)
        fun back()
        fun reload()
    }

    val action = object : Action {
        override fun toggleStartDate() {
            state.entry?.run {
                if (startDate.state == LocalDate.EMPTY) {
                    startDate.set(rememberedStartDate)
                } else {
                    rememberedStartDate = startDate.state
                    startDate.set(LocalDate.EMPTY)
                }
            }
        }

        override fun toggleEndDate() {
            state.entry?.run {
                if (endDate.state == LocalDate.EMPTY) {
                    endDate.set(rememberedEndDate)
                } else {
                    rememberedEndDate = endDate.state
                    endDate.set(LocalDate.EMPTY)
                }
            }
        }

        override fun pickMedia(aspectRatio: String) {
            val (w, h) = aspectRatio.split(":").map { it.toInt() }
            if (!mediaPickTasks.contains(aspectRatio)) mediaPickTasks.add(aspectRatio)
            ctx.navCallback.collector.pickMedia(
                nav = ctx.nav,
                key = aspectRatio,
                type = ResourceType.ALL,
                orientation = if (h > w) RotationType.VERTICAL.mask else RotationType.HORIZONTAL.mask,
                openInRootNode = false
            )
        }

        override fun nextStep() {
            val editor = state.entry ?: return
            when (currentStep) {
                0 -> {
                    if (editor.name.state.isBlank()) {
                        warningMessage = Res.string.ui_campaigns_edit_warn_name_required
                        return
                    }
                    currentStep = 1
                }

                1 -> {
                    if (editor.selectedDeviceIds.isEmpty()) {
                        warningMessage = Res.string.ui_campaigns_edit_warn_select_device
                        return
                    }
                    currentStep = 2
                }
            }
        }

        override fun prevStep() {
            if (currentStep > 0) currentStep--
            else ctx.nav.up()
        }

        override fun toggleDevice(deviceId: Long) {
            state.entry?.toggleDevice(deviceId)
        }

        override fun saveAsDraft() {
            if (validate()) save(CampaignState.DRAFT.mask)
        }

        override fun activateCampaign() {
            if (validate()) save(CampaignState.ACTIVE.mask)
        }

        override fun clearWarning() {
            warningMessage = null
        }

        override fun back() {
            ctx.nav.up()
        }

        override fun reload() {
            loadInitialData()
        }
    }

    private fun validate(): Boolean {
        val editor = state.entry ?: return false
        val aspectRatios = editor.getSelectedAspectRatios()
        if (aspectRatios.isEmpty()) {
            warningMessage = Res.string.ui_campaigns_edit_warn_select_device
            return false
        }

        if (aspectRatios.any { editor.selectedMediaByAspectRatio[it] == null }) {
            warningMessage = Res.string.ui_campaigns_edit_warn_set_assets
            return false
        }
        return true
    }

    fun setup(ctx: CampaignECtx) {
        this.ctx = ctx
        val currentState = state
        if (currentState is DataState.Loading) {
            loadInitialData()
        } else if (currentState is DataState.Result) {
            val editor = currentState.nnvl
            mediaPickTasks.forEach { aspectRatio ->
                ctx.navCallback.collector.get<NavigationCallbackProvider.Task.Media>(aspectRatio) { task ->
                    task.response?.also { mediaInfo ->
                        editor.selectedMediaByAspectRatio[aspectRatio] = mediaInfo
                        val videoVisuals = editor.selectedMediaByAspectRatio.values.filterNotNull()
                            .filter { ResourceType[it.type] == ResourceType.VIDEO }
                        if (videoVisuals.isNotEmpty()) {
                            val maxVideoDurationMs = videoVisuals.maxOf { it.duration }
                            editor.durationSeconds = (maxVideoDurationMs / 1000).toInt()
                        }
                    }
                }
            }
        }
    }

    private fun loadInitialData() {
        state = DataState.Loading()
        scope.launch {
            runCatching {
                // 1. Load all devices to get aspect ratios
                val deviceDetails = loadAllDevices()

                // 2. Load all locations with devices recursively
                val locations = loadAllLocations()

                // 3. If edit mode, load campaign
                if (ctx.args.id > 0) {
                    val campaign = Repository.ads.campaign.get(ctx.args.id)
                    val info = SharedIO.CampaignInfo(
                        id = campaign.id,
                        name = campaign.name,
                        dStart = campaign.dStart,
                        dEnd = campaign.dEnd,
                        state = campaign.state,
                        visual = campaign.visual,
                        impressions = campaign.impressions,
                        device = emptyList(), // Not used in editor
                        location = emptyList() // Not used in editor
                    )
                    CampaignECtx.Editor(
                        scope = scope,
                        campaign = info,
                        locations = locations,
                        deviceDetails = deviceDetails
                    )
                } else {
                    val info = SharedIO.CampaignInfo(
                        id = -1L,
                        name = "",
                        dStart = LocalDate.EMPTY,
                        dEnd = LocalDate.EMPTY,
                        state = CampaignState.DRAFT.mask,
                        visual = emptyList(),
                        impressions = SharedIO.Impressions(0, 0),
                        device = emptyList(),
                        location = emptyList()
                    )
                    CampaignECtx.Editor(
                        scope = scope,
                        campaign = info,
                        locations = locations,
                        deviceDetails = deviceDetails
                    )
                }
            }.onSuccess { editor ->
                state = DataState.Result(editor)
            }.onFailure {
                state = DataState.Error(it.message ?: "Unknown error")
            }
        }
    }

    private suspend fun loadAllDevices(): Map<Long, SharedIO.DeviceSimple> {
        val details = mutableMapOf<Long, SharedIO.DeviceSimple>()
        var page = 0
        do {
            val result = Repository.ads.device.listing(page)
            result.items.forEach { dev ->
                details[dev.id] = dev
            }
            page++
        } while (details.size < result.ids.size && result.items.isNotEmpty())
        return details
    }

    private suspend fun loadAllLocations(): List<SharedIO.LocationWithDevices> {
        var page = 0
        return buildList {
            do {
                val result = Repository.ads.location.listingWithDevices(page)
                addAll(result.items)
                page++
            } while (result.count != count())
            removeAll { it.devices.isEmpty() }
        }.sortedBy { it.name.noDiacriticsLower }
    }

    private fun save(stateMask: Int) {
        val editor = state.entry ?: return
        scope.launch {
            runCatching {
                val ar = editor.getSelectedAspectRatios()

                val visual = editor.selectedMediaByAspectRatio
                    .filter { it.key in ar }
                    .map { entry ->
                        val media = entry.value!!
                        val durationMs = if (ResourceType[media.type] == ResourceType.IMAGE) {
                            val seconds = if (editor.durationSeconds < 10) 10 else editor.durationSeconds
                            seconds * 1000L
                        } else {
                            media.duration
                        }
                        SharedIO.CampaignCore.Entry(
                            id = media.id,
                            duration = durationMs,
                            devices = editor.getDeviceIdsForAspectRatio(entry.key).toSet()
                        )
                    }

                val body = editor.data.copy(state = stateMask, visual = visual)
                
                if(body.dStart == LocalDate.EMPTY){
                    body.dStart = Clock.nowDate()
                }

                if (ctx.args.id > 0) {
                    Repository.ads.campaign.change(ctx.args.id, body)
                } else {
                    Repository.ads.campaign.add(body)
                }
            }.onSuccess {
                ctx.nav.up()
            }.onFailure {
                warningMessage = Res.string.ui_campaigns_edit_warn_save_failed
            }
        }
    }

    override fun onRelease() {}
}